import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, qs } from '../lib/api'
import { useAuth } from '../lib/auth'
import { count, dateOnly, money, sentence } from '../lib/format'
import type { Invoice, ListResponse, PricePlan, Subscription, Tenant } from '../lib/types'
import { Page } from '../components/Shell'
import { Icon } from '../components/Icon'
import { DataTable, Toolbar, type Column } from '../components/DataTable'
import { Button, Chip, Field, Metric, Modal, Panel, Toggle, toast } from '../components/ui'

const STATUS_TONE: Record<string, 'ok' | 'accent' | 'warn' | 'critical' | 'muted'> = {
  paid: 'ok',
  issued: 'accent',
  draft: 'muted',
  overdue: 'critical',
  void: 'muted',
}

export function Billing() {
  const { can, user } = useAuth()
  const [tab, setTab] = useState<'invoices' | 'plans' | 'subscriptions'>('invoices')

  const tabs = [
    { key: 'invoices' as const, label: 'Invoices', show: can('invoice.view') },
    { key: 'plans' as const, label: 'Price plans', show: can('pricing.retail') || can('platform.pricing.wholesale') },
    { key: 'subscriptions' as const, label: 'Subscriptions', show: can('invoice.view') },
  ].filter((t) => t.show)

  return (
    <Page
      title="Billing"
      subtitle={
        user?.tier === 3
          ? 'Your subscription and invoices'
          : 'What you charge, who owes you, and what they owe'
      }
      actions={
        <div className="flex gap-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className="h-8 px-3 rounded-[3px] border text-[12px] font-[var(--font-label)] font-semibold uppercase tracking-wider transition-colors"
              style={{
                borderColor: tab === t.key ? 'var(--accent)' : 'var(--color-rule)',
                background: tab === t.key ? 'var(--accent-dim)' : 'transparent',
                color: tab === t.key ? 'var(--accent-text)' : 'var(--color-ink-3)',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      }
    >
      {tab === 'invoices' && <Invoices />}
      {tab === 'plans' && <Plans />}
      {tab === 'subscriptions' && <Subscriptions />}
    </Page>
  )
}

/* ===========================================================================
   Invoices
   =========================================================================== */

function Invoices() {
  const { can } = useAuth()
  const qc = useQueryClient()
  const [status, setStatus] = useState('')
  const [offset, setOffset] = useState(0)
  const [viewing, setViewing] = useState<Invoice | null>(null)
  const [generating, setGenerating] = useState(false)
  const limit = 50

  const list = useQuery({
    queryKey: ['invoices', status, offset],
    queryFn: () => api<ListResponse<Invoice>>(`/billing/invoices${qs({ status, limit, offset })}`),
  })

  const issue = useMutation({
    mutationFn: (id: string) => api(`/billing/invoices/${id}/issue`, { method: 'POST' }),
    onSuccess: () => {
      toast('Invoice issued', 'ok')
      void qc.invalidateQueries({ queryKey: ['invoices'] })
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  const rows = list.data?.items ?? []
  const outstanding = rows
    .filter((i) => i.status === 'issued' || i.status === 'overdue')
    .reduce((sum, i) => sum + (i.totalCents - i.paidCents), 0)
  const overdue = rows.filter((i) => i.status === 'overdue').length

  const columns: Column<Invoice>[] = [
    { key: 'number', header: 'Number', width: '150px', render: (i) => <span className="tnum">{i.number}</span> },
    { key: 'billTo', header: 'Billed to', render: (i) => i.billToName ?? '—' },
    {
      key: 'period',
      header: 'Period',
      render: (i) => (
        <span className="text-[12px]">
          {dateOnly(i.periodStart)} — {dateOnly(i.periodEnd)}
        </span>
      ),
    },
    {
      key: 'total',
      header: 'Total',
      numeric: true,
      render: (i) => <span className="text-[var(--color-ink)]">{money(i.totalCents, i.currency)}</span>,
    },
    {
      key: 'outstanding',
      header: 'Outstanding',
      numeric: true,
      render: (i) =>
        i.totalCents - i.paidCents > 0 ? (
          <span style={{ color: i.status === 'overdue' ? 'var(--text-critical)' : undefined }}>
            {money(i.totalCents - i.paidCents, i.currency)}
          </span>
        ) : (
          <span className="text-[var(--color-ink-4)]">—</span>
        ),
    },
    {
      key: 'due',
      header: 'Due',
      numeric: true,
      render: (i) => (i.dueAt ? dateOnly(i.dueAt) : '—'),
    },
    {
      key: 'status',
      header: 'Status',
      width: '90px',
      render: (i) => <Chip tone={STATUS_TONE[i.status] ?? 'muted'}>{i.status}</Chip>,
    },
    {
      key: 'actions',
      header: '',
      width: '90px',
      render: (i) => (
        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          {i.status === 'draft' && can('invoice.issue') && (
            <Button size="sm" onClick={() => issue.mutate(i.id)}>
              Issue
            </Button>
          )}
          {(i.status === 'issued' || i.status === 'overdue') && can('invoice.issue') && (
            <Button size="sm" onClick={() => setViewing(i)}>
              Pay
            </Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <>
      {rows.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
          <Metric label="Outstanding" value={money(outstanding, rows[0]?.currency)} tone={outstanding > 0 ? 'accent' : 'default'} />
          <Metric label="Overdue invoices" value={count(overdue)} tone={overdue > 0 ? 'critical' : 'default'} />
          <Metric label="On this page" value={count(rows.length)} hint={`of ${count(list.data?.total)}`} />
        </div>
      )}

      <Toolbar
        actions={
          can('invoice.issue') && (
            <Button variant="primary" onClick={() => setGenerating(true)}>
              <Icon name="plus" size={13} />
              Generate invoice
            </Button>
          )
        }
      >
        <select className="field w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {['draft', 'issued', 'paid', 'overdue', 'void'].map((s) => (
            <option key={s} value={s}>
              {sentence(s)}
            </option>
          ))}
        </select>
      </Toolbar>

      <Panel padded={false}>
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(i) => i.id}
          loading={list.isLoading}
          error={list.error}
          onRowClick={(i) => setViewing(i)}
          total={list.data?.total}
          limit={limit}
          offset={offset}
          onPage={setOffset}
          empty={{
            title: 'No invoices',
            body: 'Invoices are generated monthly from active subscriptions, as drafts for you to review.',
          }}
        />
      </Panel>

      {viewing && (
        <InvoiceModal
          invoice={viewing}
          onClose={() => setViewing(null)}
          onPaid={() => {
            setViewing(null)
            void qc.invalidateQueries({ queryKey: ['invoices'] })
          }}
        />
      )}

      {generating && (
        <GenerateModal
          onClose={() => setGenerating(false)}
          onDone={() => {
            setGenerating(false)
            void qc.invalidateQueries({ queryKey: ['invoices'] })
          }}
        />
      )}
    </>
  )
}

function InvoiceModal({
  invoice,
  onClose,
  onPaid,
}: {
  invoice: Invoice
  onClose: () => void
  onPaid: () => void
}) {
  const { can } = useAuth()
  const [amount, setAmount] = useState(((invoice.totalCents - invoice.paidCents) / 100).toFixed(2))
  const [method, setMethod] = useState('transfer')
  const [reference, setReference] = useState('')

  const full = useQuery({
    queryKey: ['invoice', invoice.id],
    queryFn: () => api<Invoice>(`/billing/invoices/${invoice.id}`),
  })

  const pay = useMutation({
    mutationFn: () =>
      api(`/billing/invoices/${invoice.id}/payment`, {
        body: {
          amountCents: Math.round(Number(amount) * 100),
          method,
          reference: reference.trim(),
        },
      }),
    onSuccess: () => {
      toast('Payment recorded', 'ok')
      onPaid()
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  const outstanding = invoice.totalCents - invoice.paidCents
  const canPay = can('invoice.issue') && outstanding > 0

  return (
    <Modal
      open
      onClose={onClose}
      title={`Invoice ${invoice.number}`}
      width={640}
      footer={
        canPay ? (
          <>
            <Button onClick={onClose}>Close</Button>
            <Button variant="primary" disabled={pay.isPending || Number(amount) <= 0} onClick={() => pay.mutate()}>
              {pay.isPending ? 'Recording…' : `Record ${money(Math.round(Number(amount) * 100), invoice.currency)}`}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Summary label="Billed to" value={invoice.billToName ?? '—'} />
          <Summary label="Period" value={`${dateOnly(invoice.periodStart)} — ${dateOnly(invoice.periodEnd)}`} />
          <Summary label="Due" value={invoice.dueAt ? dateOnly(invoice.dueAt) : '—'} />
          <Summary label="Status" value={sentence(invoice.status)} />
        </div>

        <div className="panel" style={{ background: 'var(--color-hull)' }}>
          <table className="grid-table">
            <thead>
              <tr>
                <th>Description</th>
                <th style={{ textAlign: 'right' }}>Qty</th>
                <th style={{ textAlign: 'right' }}>Unit</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {full.data?.lines?.length ? (
                full.data.lines.map((l) => (
                  <tr key={l.id}>
                    <td>{l.description}</td>
                    <td className="tnum text-right">{l.quantity}</td>
                    <td className="tnum text-right">{money(l.unitPriceCents, invoice.currency)}</td>
                    <td className="tnum text-right text-[var(--color-ink)]">
                      {money(l.amountCents, invoice.currency)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center text-[var(--color-ink-4)] py-6">
                    {full.isLoading ? 'Loading lines…' : 'No line items'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="px-3 py-2.5 rule-t space-y-1.5">
            <Total label="Subtotal" value={money(invoice.subtotalCents, invoice.currency)} />
            {invoice.taxCents > 0 && <Total label="Tax" value={money(invoice.taxCents, invoice.currency)} />}
            <Total label="Total" value={money(invoice.totalCents, invoice.currency)} strong />
            {invoice.paidCents > 0 && (
              <Total label="Paid" value={`− ${money(invoice.paidCents, invoice.currency)}`} tone="ok" />
            )}
            {outstanding > 0 && (
              <Total
                label="Outstanding"
                value={money(outstanding, invoice.currency)}
                strong
                tone={invoice.status === 'overdue' ? 'critical' : undefined}
              />
            )}
          </div>
        </div>

        {canPay && (
          <div className="rule-t pt-4">
            <div className="label mb-3">Record a payment</div>
            <div className="grid sm:grid-cols-3 gap-3.5">
              <Field label="Amount">
                <input className="field tnum" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
              </Field>
              <Field label="Method">
                <select className="field" value={method} onChange={(e) => setMethod(e.target.value)}>
                  {['transfer', 'card', 'mandate', 'manual'].map((m) => (
                    <option key={m} value={m}>
                      {sentence(m)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Reference">
                <input className="field" value={reference} onChange={(e) => setReference(e.target.value)} />
              </Field>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

function GenerateModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [tenantId, setTenantId] = useState('')
  const [taxRate, setTaxRate] = useState('21')
  const [dueInDays, setDueInDays] = useState('14')

  const tenants = useQuery({
    queryKey: ['tenants', 'billable'],
    queryFn: () => api<ListResponse<Tenant>>(`/tenants${qs({ limit: 200 })}`),
  })

  const generate = useMutation({
    mutationFn: () =>
      api<Invoice>('/billing/invoices', {
        body: {
          billToTenantId: tenantId,
          taxRateBp: Math.round(Number(taxRate) * 100),
          dueInDays: Number(dueInDays),
        },
      }),
    onSuccess: (inv) => {
      toast(`Draft ${inv.number} created`, 'ok')
      onDone()
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  return (
    <Modal
      open
      onClose={onClose}
      title="Generate invoice"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!tenantId || generate.isPending} onClick={() => generate.mutate()}>
            {generate.isPending ? 'Generating…' : 'Create draft'}
          </Button>
        </>
      }
    >
      <div className="space-y-3.5">
        <p className="text-[13px] text-[var(--color-ink-2)] leading-relaxed">
          Bills last calendar month from the account's active subscriptions. It is created as a
          draft — nothing is sent until you issue it.
        </p>

        <Field label="Account" required>
          <select className="field" value={tenantId} onChange={(e) => setTenantId(e.target.value)} autoFocus>
            <option value="">Choose an account…</option>
            {tenants.data?.items.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-3.5">
          <Field label="Tax rate" hint="percent">
            <input className="field tnum" type="number" step="0.01" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} />
          </Field>
          <Field label="Payment terms" hint="days">
            <input className="field tnum" type="number" value={dueInDays} onChange={(e) => setDueInDays(e.target.value)} />
          </Field>
        </div>
      </div>
    </Modal>
  )
}

/* ===========================================================================
   Price plans
   =========================================================================== */

function Plans() {
  const { can, user } = useAuth()
  const qc = useQueryClient()
  const [creating, setCreating] = useState(false)

  const list = useQuery({
    queryKey: ['plans'],
    queryFn: () => api<{ items: PricePlan[] }>('/billing/plans'),
  })

  const wholesale = user?.tier === 1

  const columns: Column<PricePlan>[] = [
    {
      key: 'name',
      header: 'Plan',
      render: (p) => (
        <div>
          <div className="text-[var(--color-ink)]">{p.name}</div>
          {p.description && <div className="text-[11px] text-[var(--color-ink-4)]">{p.description}</div>}
        </div>
      ),
    },
    {
      key: 'price',
      header: 'Per device',
      numeric: true,
      render: (p) => (
        <span className="text-[var(--color-ink)]">
          {money(p.unitPriceCents, p.currency)}
          <span className="text-[var(--color-ink-4)]">/{p.billingPeriod === 'monthly' ? 'mo' : p.billingPeriod === 'annual' ? 'yr' : 'qtr'}</span>
        </span>
      ),
    },
    { key: 'min', header: 'Minimum', numeric: true, render: (p) => (p.minUnits ? `${p.minUnits} units` : '—') },
    {
      key: 'setup',
      header: 'Setup fee',
      numeric: true,
      render: (p) => (p.setupFeeCents ? money(p.setupFeeCents, p.currency) : '—'),
    },
    {
      key: 'retention',
      header: 'Retention',
      numeric: true,
      render: (p) => (p.dataRetentionDays ? `${p.dataRetentionDays}d` : '—'),
    },
    { key: 'subs', header: 'Subscribers', numeric: true, render: (p) => count(p.subscribers) },
    {
      key: 'active',
      header: 'State',
      width: '80px',
      render: (p) => (p.active ? <Chip tone="ok">On sale</Chip> : <Chip tone="muted">Retired</Chip>),
    },
  ]

  return (
    <>
      <Toolbar
        actions={
          can(wholesale ? 'platform.pricing.wholesale' : 'pricing.retail') && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Icon name="plus" size={13} />
              New plan
            </Button>
          )
        }
      />

      <Panel padded={false}>
        <DataTable
          columns={columns}
          rows={list.data?.items}
          rowKey={(p) => p.id}
          loading={list.isLoading}
          error={list.error}
          empty={{
            title: 'No price plans',
            body: wholesale
              ? 'Define what you charge resellers per device per month.'
              : 'Define what you charge your clients per device per month.',
          }}
        />
      </Panel>

      {creating && (
        <PlanForm
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            void qc.invalidateQueries({ queryKey: ['plans'] })
          }}
        />
      )}
    </>
  )
}

function PlanForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    unitPrice: '',
    currency: 'EUR',
    billingPeriod: 'monthly',
    minUnits: '0',
    setupFee: '0',
    dataRetentionDays: '',
    active: true,
  })

  const create = useMutation({
    mutationFn: () =>
      api('/billing/plans', {
        body: {
          name: form.name.trim(),
          description: form.description.trim() || null,
          unitPriceCents: Math.round(Number(form.unitPrice) * 100),
          currency: form.currency,
          billingPeriod: form.billingPeriod,
          minUnits: Number(form.minUnits) || 0,
          setupFeeCents: Math.round(Number(form.setupFee) * 100),
          dataRetentionDays: form.dataRetentionDays ? Number(form.dataRetentionDays) : null,
          features: {},
          volumeTiers: [],
        },
      }),
    onSuccess: () => {
      toast('Plan created', 'ok')
      onSaved()
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  return (
    <Modal
      open
      onClose={onClose}
      title="New price plan"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!form.name.trim() || !form.unitPrice || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'Creating…' : 'Create plan'}
          </Button>
        </>
      }
    >
      <div className="space-y-3.5">
        <Field label="Name" required>
          <input className="field" value={form.name} onChange={set('name')} placeholder="Standard tracking" autoFocus />
        </Field>
        <Field label="Description">
          <input className="field" value={form.description} onChange={set('description')} />
        </Field>

        <div className="grid sm:grid-cols-3 gap-3.5">
          <Field label="Price per device" required>
            <input className="field tnum" type="number" step="0.01" value={form.unitPrice} onChange={set('unitPrice')} />
          </Field>
          <Field label="Currency">
            <select className="field" value={form.currency} onChange={set('currency')}>
              {['EUR', 'USD', 'GBP', 'ZAR'].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Billed">
            <select className="field" value={form.billingPeriod} onChange={set('billingPeriod')}>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annually</option>
            </select>
          </Field>
        </div>

        <div className="grid sm:grid-cols-3 gap-3.5">
          <Field label="Minimum units">
            <input className="field tnum" type="number" value={form.minUnits} onChange={set('minUnits')} />
          </Field>
          <Field label="Setup fee">
            <input className="field tnum" type="number" step="0.01" value={form.setupFee} onChange={set('setupFee')} />
          </Field>
          <Field label="Data retention" hint="days">
            <input className="field tnum" type="number" value={form.dataRetentionDays} onChange={set('dataRetentionDays')} placeholder="90" />
          </Field>
        </div>

        <Toggle checked={form.active} onChange={(v) => setForm((f) => ({ ...f, active: v }))} label="Available to sell" />
      </div>
    </Modal>
  )
}

/* ===========================================================================
   Subscriptions
   =========================================================================== */

function Subscriptions() {
  const [offset, setOffset] = useState(0)
  const limit = 50

  const list = useQuery({
    queryKey: ['subscriptions', offset],
    queryFn: () => api<ListResponse<Subscription>>(`/billing/subscriptions${qs({ limit, offset })}`),
  })

  const columns: Column<Subscription>[] = [
    { key: 'imei', header: 'Device', render: (s) => <span className="tnum">{s.imei ?? '—'}</span> },
    { key: 'plan', header: 'Plan', render: (s) => s.planName ?? '—' },
    {
      key: 'price',
      header: 'Price',
      numeric: true,
      render: (s) => (s.unitPriceCents != null ? money(s.unitPriceCents) : <span className="text-[var(--color-ink-4)]">plan rate</span>),
    },
    { key: 'qty', header: 'Qty', numeric: true, render: (s) => s.quantity },
    {
      key: 'status',
      header: 'Status',
      width: '100px',
      render: (s) => (
        <Chip tone={s.status === 'active' ? 'ok' : s.status === 'past_due' ? 'warn' : 'muted'}>
          {s.status.replace('_', ' ')}
        </Chip>
      ),
    },
    { key: 'since', header: 'Since', numeric: true, render: (s) => dateOnly(s.startedAt) },
    { key: 'renews', header: 'Renews', numeric: true, render: (s) => dateOnly(s.currentPeriodEnd) },
  ]

  return (
    <Panel padded={false}>
      <DataTable
        columns={columns}
        rows={list.data?.items}
        rowKey={(s) => s.id}
        loading={list.isLoading}
        error={list.error}
        total={list.data?.total}
        limit={limit}
        offset={offset}
        onPage={setOffset}
        empty={{
          title: 'No subscriptions',
          body: 'A device becomes billable when it is subscribed to a plan.',
        }}
      />
    </Panel>
  )
}

/* --- bits ----------------------------------------------------------------- */

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="label text-[10px] mb-1">{label}</div>
      <div className="text-[13px] text-[var(--color-ink)]">{value}</div>
    </div>
  )
}

function Total({
  label,
  value,
  strong,
  tone,
}: {
  label: string
  value: string
  strong?: boolean
  tone?: 'ok' | 'critical'
}) {
  const colors = { ok: 'var(--text-ok)', critical: 'var(--text-critical)' }
  return (
    <div className="flex justify-between items-baseline">
      <span className={strong ? 'label label-bright' : 'label'}>{label}</span>
      <span
        className="tnum"
        style={{
          fontSize: strong ? '14px' : '12px',
          fontWeight: strong ? 600 : 400,
          color: tone ? colors[tone] : strong ? 'var(--color-ink)' : 'var(--color-ink-2)',
        }}
      >
        {value}
      </span>
    </div>
  )
}
